
import Vue from 'vue';
import axios from 'axios';
import moment from 'moment';
import EventService from '../services/EventService';
import {mapState} from 'vuex';
import store from '../store';
import {saveAs} from 'file-saver';
import type {User} from 'firebase/auth';

const baseURL = EventService.ProtoURL();
const apiClient = axios.create({
  baseURL: baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(config => {
  const companyID = store.state.current;
  if (companyID !== '') {
    config.headers['x-company-id'] = companyID;
  }
  return config;
});

type FileCtx = {
  name: string;
  ID: string;
};

type History = {
  audit: {
    summary: string;
  };
  id: string;
  taskCount: number;
  schedule: {
    date: string;
    start: string;
    end: string;
    place: string;
  };
};

type Item = {
  date: string;
  place: string;
  time: string;
  summary: string;
  taskCount: number;
  id: string;
};

type Data = {
  currentFileCtxList: Array<FileCtx>;
  page: number;
  itemsPerPage: number;
  items: Array<Item>;
};

type Method = {
  list(docid: string): Promise<void>;
  download(docid: string, id: string, name: string): Promise<void>;
  cardClick(id: string): void;
};

type Computed = {
  itemsSlice: Array<Item>;
  user: User | undefined;
};

const getItems = async (token: string): Promise<Array<Item>> => {
  let items = new Array<Item>();
  if (token) {
    const {data} = await apiClient.get('/histories/', {
      headers: {Authorization: `Bearer ${token}`},
    });
    console.log(data);
    items = data.map((history: History) => {
      return {
        date: history.schedule.date,
        place: history.schedule.place,
        time: `${history.schedule.start}-${history.schedule.end}`,
        summary: history.audit.summary,
        id: history.id,
        taskCount: history.taskCount,
      };
    });
    items
      .sort((a, b) => {
        const ma = moment(a.date);
        const mb = moment(b.date);
        if (ma.isBefore(mb)) {
          return -1;
        }
        if (ma.isAfter(mb)) {
          return 1;
        }
        return 0;
      })
      .reverse();
  }
  return items;
};

export default Vue.extend<Data, Method, Computed>({
  computed: {
    itemsSlice: function () {
      return this.items.slice((this.page - 1) * 6, this.page * 6);
    },
    ...mapState(['user']),
  },
  async mounted() {
    if (this.user) {
      try {
        const token = await this.user.getIdToken();
        this.items = await getItems(token);
      } catch (e) {
        console.log(e);
      }
    }
  },
  data: (): Data => ({
    currentFileCtxList: [],
    page: 1,
    itemsPerPage: 6,
    items: [],
  }),
  methods: {
    async list(docid: string): Promise<void> {
      const token = await this.user?.getIdToken();
      try {
        const {data} = await apiClient.get(`/histories/files/${docid}/list/`, {
          headers: {Authorization: `Bearer ${token}`},
        });
        if (data) {
          this.currentFileCtxList = data;
        } else {
          this.currentFileCtxList = [];
        }
      } catch (err) {
        this.currentFileCtxList = [];
        console.log(err);
      }
    },
    async download(docid: string, id: string, name: string): Promise<void> {
      const token = await this.user?.getIdToken();
      try {
        const {data} = await apiClient.get(
          `/histories/files/${docid}/list/${id}`,
          {
            headers: {Authorization: `Bearer ${token}`},
            responseType: 'blob',
          }
        );
        console.log(data);
        saveAs(new Blob([data]), name);
      } catch (err) {
        console.log(err);
      }
    },
    cardClick(id: string) {
      this.$router.push(`/audithistory/${id}`);
    },
  },
});
